import cn from 'classnames';
import React from 'react';
import { useIntl } from 'react-intl';
import { FeeSolutionPlan } from 'sonata-static-storage/lib/schemas/feeSolution/feeSolutionPlan.schema';

import { CapabilitiesInvoiceStyled } from './CapabilitiesInvoice.styled.ts';

export type CategoryPrice = {
  id: string;
  items: {
    id: string;
    name: string;
    price: number;
    type?: 'strikethrough' | 'bold';
  }[];
  name: string;
};

type Props = {
  categoriesPrice: CategoryPrice[];
  formatPrice: (val: number) => string;
  id: string;
  overallMaxCost?: number;
  plan?: FeeSolutionPlan;
  solutionName: string;
  solutionPrice: number;
  totalPaid: number;
  totalPrice: number;
};

const CapabilitiesInvoiceView: React.VFC<Props> = ({
  categoriesPrice,
  formatPrice,
  id,
  overallMaxCost,
  plan,
  solutionName,
  solutionPrice,
  totalPaid,
  totalPrice
}) => {
  const { formatMessage } = useIntl();

  return (
    <CapabilitiesInvoiceStyled className="capabilitiesInvoice" data-testid={id} id={id}>
      <div className="capabilitiesInvoice__title">
        {formatMessage({
          defaultMessage: 'Total Invoice',
          id: 'feesBySolution.stepThree.invoice.title'
        })}
      </div>
      <div className="capabilitiesInvoice__solution">
        <div className="capabilitiesInvoice__solutionItem">{`${solutionName} / ${plan?.copy}`}</div>
        <div className="capabilitiesInvoice__solutionPrice">{`+${formatPrice(solutionPrice)}`}</div>
      </div>
      <div className="capabilitiesInvoice__categories">
        {categoriesPrice.map((category, index) => (
          <div className="capabilitiesInvoice__category" key={index}>
            <div className="capabilitiesInvoice__categoryTitle">{category.name}</div>
            {category.items.map((item, key) => (
              <div className="capabilitiesInvoice__categoryContainer" key={key}>
                <div
                  className={cn(
                    { capabilitiesInvoice__categoryContainerItem: !item.type },
                    { [`capabilitiesInvoice__categoryContainerItem__${item.type}`]: item.type }
                  )}
                >
                  {item.name}
                </div>
                <div
                  className={cn(
                    { capabilitiesInvoice__categoryContainerPrice: !item.type },
                    { [`capabilitiesInvoice__categoryContainerPrice__${item.type}`]: item.type }
                  )}
                >
                  {`+${formatPrice(item.price)}`}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="capabilitiesInvoice__total">
        <div className="capabilitiesInvoice__totalItem">
          {formatMessage({
            defaultMessage: 'Total',
            id: 'feesBySolution.stepThree.invoice.total'
          })}
        </div>
        <div className="capabilitiesInvoice__totalPrice">{formatPrice(totalPrice)}</div>
      </div>
      {overallMaxCost !== undefined && overallMaxCost < totalPrice && (
        <div className="capabilitiesInvoice__total">
          <div className="capabilitiesInvoice__totalItem">
            {formatMessage({
              defaultMessage: 'Overall Max Cost',
              id: 'feesBySolution.stepThree.invoice.overall'
            })}
          </div>
          <div className="capabilitiesInvoice__totalPrice">{formatPrice(overallMaxCost)}</div>
        </div>
      )}
      <div className="capabilitiesInvoice__paid">
        <div className="capabilitiesInvoice__paidItem">
          {formatMessage({
            defaultMessage: 'Total to be paid',
            id: 'feesBySolution.stepThree.invoice.paid'
          })}
        </div>
        <div className="capabilitiesInvoice__paidPrice">{formatPrice(totalPaid)}</div>
      </div>
    </CapabilitiesInvoiceStyled>
  );
};

export default CapabilitiesInvoiceView;
